import type { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AuthBranding } from '../components/AuthBranding';
import { LanguageSwitcher } from '@/shared/components/LanguageSwitcher';
import { useAuthStore } from '@/features/auth/store';

interface AuthLayoutProps {
    children: ReactNode;
    branding?: {
        title?: string;
        subtitle?: string;
        features?: string[];
    };
    showBranding?: boolean;
    showLogout?: boolean;
}

export function AuthLayout({
    children,
    branding,
    showBranding = true,
    showLogout = false,
}: AuthLayoutProps) {
    const { t } = useTranslation();
    const logout = useAuthStore((s) => s.logout);

    if (!showBranding) {
        return (
            <div
                className="min-h-screen min-h-dvh flex flex-col bg-background"
                style={{ paddingTop: 'env(safe-area-inset-top)' }}
            >
                <div className="shrink-0 flex items-center justify-between px-5 py-4 border-b border-border/50">
                    <div className="flex items-center gap-2.5">
                        <img src="/logo.svg" alt="Restroos" className="h-8 w-8 rounded-lg" />
                        <span className="text-base font-semibold tracking-tight">
                            <span className="text-foreground">Res</span>
                            <span className="text-accent">troos</span>
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        {showLogout && (
                            <button
                                type="button"
                                onClick={() => logout()}
                                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                            >
                                <LogOut className="w-4 h-4" />
                                {t('common.logout')}
                            </button>
                        )}
                        <LanguageSwitcher />
                    </div>
                </div>
                <div className="flex-1 px-6 py-10 sm:px-8 sm:py-12 flex flex-col items-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="w-full max-w-md"
                    >
                        {children}
                    </motion.div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen min-h-dvh flex flex-col lg:flex-row bg-background">
            {/* Mobile hero — visible only below lg breakpoint */}
            <div className="relative lg:hidden">
                <AuthBranding variant="mobile-hero" {...branding} />
                {/* Language switcher overlaid on the hero, safe-area aware */}
                <div
                    className="absolute right-4 z-20"
                    style={{ top: 'calc(env(safe-area-inset-top) + 0.875rem)' }}
                >
                    <LanguageSwitcher />
                </div>
            </div>

            {/* Desktop sidebar — visible only at lg+ */}
            <AuthBranding variant="sidebar" {...branding} />

            {/* Form area — natural block flow on mobile so the window scrolls;
                flex-centered on desktop where the form always fits in viewport. */}
            <div className="flex-1 relative px-6 py-10 sm:px-8 sm:py-12 lg:flex lg:items-center lg:justify-center lg:p-12">
                <div
                    className="hidden lg:block absolute right-6"
                    style={{ top: 'max(env(safe-area-inset-top), 1.25rem)' }}
                >
                    <LanguageSwitcher />
                </div>
                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="w-full max-w-md mx-auto"
                >
                    {children}
                </motion.div>
            </div>
        </div>
    );
}
